import { Button, Dropdown, Flex, Tooltip, Space, Typography } from 'antd';
import Icon, { EllipsisOutlined, WifiOutlined } from '@ant-design/icons';

import { TRANSACTION_TYPE } from 'utilities/constants';
import { formatDateDescriptive } from 'utilities/datesAndTimes';
import { formatDollarsFromCents } from 'utilities/helpers';
import { getColumnSearchProps } from 'utilities/tableHelpers';
import _get from 'lodash/get';
import _uniqBy from 'lodash/uniqBy';

export const ACTION_KEY = Object.freeze({
  REVERSE_AUTHORIZATION: 'REVERSE_AUTHORIZATION',
  WAIVE_FEE: 'WAIVE_FEE',
});

const FEE_DESCRIPTIONS = Object.freeze([
  'CASH ADVANCE FEE', // 0401
  'LATE FEE', // 0402
  'INTEREST CHARGE-PURCHASES', // 0403
  'INTEREST CHARGE-CASH', // 0404
  'MINIMUM CHARGE', // 0403, 0404
  'ANNUAL FEE', // 0405
  'RETURNED PAYMENT FEE', // 0421
  'INTERNATIONAL TRANS FEE', // 0855
  'MEMBERSHIP FEE', // 0860
  'MONTHLY FEE', // 0861
]);

const searchFilter = (dataIndex, value, record) => {
  const indexValue = _get(record, dataIndex, '');
  if (Number(value)) {
    return Math.abs(indexValue) === Math.round(Number(value) * 100);
  }

  return indexValue?.toString().toLowerCase().includes(value.toLowerCase());
};

export const getTableColumns = (
  transactions,
  showAdditionalActions = false,
  accountInDebtSale = false,
  enableOTBRelease = false,
  { defaultAction, menuItemAction },
) => {
  // If auth history entries are in the array it should be safe to assume
  // all entries are auth history
  const containsAuthHistory = transactions.find(
    (transaction) => transaction.isFraudAuthorization,
  );

  const stateFilterOptions = _uniqBy(
    transactions.map((entry) => ({
      text: entry.state,
      value: entry.state,
    })),
    'value',
  );

  const statusFilterOptions = _uniqBy(
    transactions.map((entry) => ({
      text: entry.activityStatus,
      value: entry.activityStatus,
    })),
    'value',
  );

  const typeFilterOptions = _uniqBy(
    transactions.map((entry) => ({
      text: entry.type,
      value: entry.type,
    })),
    'value',
  );

  let columns = [
    {
      title: 'Authorization Date',
      defaultSortOrder: 'descend',
      render: (entry) => {
        // As far as I can tell, date on standard transaction results need to be processed
        // as UTC but authorization history results do not. Very cool.
        return formatDateDescriptive(entry.date, !entry.isFraudAuthorization);
      },
      sorter: (a, b) => {
        return a.date - b.date;
      },
    },
    {
      title: 'Posted Date',
      render: (entry) => {
        return formatDateDescriptive(entry.postedAt, true, '-');
      },
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.postedAt - b.postedAt,
    },
    {
      title: 'Merchant Channel',
      dataIndex: 'merchantChannel',
      ...getColumnSearchProps('merchantChannel', searchFilter),
    },
    {
      title: 'Amount',
      render: (transaction) => formatDollarsFromCents(transaction.amount),
      ...getColumnSearchProps('amount', searchFilter),
    },
  ];

  if (containsAuthHistory) {
    columns.push({
      title: 'Is Fraud',
      filters: [
        {
          text: 'Yes',
          value: true,
        },
        {
          text: 'No',
          value: false,
        },
      ],
      render: (entry) => (entry.fraud ? 'Yes' : 'No'),
      onFilter: (option, entry) => entry.fraud === option,
    });
  } else {
    const cardLast4FilterOptions = _uniqBy(
      transactions.map((entry) => ({
        text: entry.cardLast4 || '-',
        value: entry.cardLast4 || '-',
      })),
      'value',
    );
    columns.push(
      {
        title: 'Card Last 4',
        filters: cardLast4FilterOptions,
        render: (entry) => {
          return (
            <Flex align="center" gap="var(--spacing-xs)">
              <Typography.Text> {entry.cardLast4 || '-'} </Typography.Text>
              {entry.virtualCardLast4 && (
                <Tooltip
                  title={'Digital Wallet Token: ' + entry.virtualCardLast4}
                >
                  <Icon
                    component={WifiOutlined}
                    style={{
                      color: 'var(--green-4)',
                      fontSize: '0.75rem',
                    }}
                  ></Icon>
                </Tooltip>
              )}
            </Flex>
          );
        },
        onFilter: (option, entry) => {
          return (entry.cardLast4 || '-') === option;
        },
      },
      {
        title: 'Status',
        dataIndex: 'activityStatus',
        filters: statusFilterOptions,
        onFilter: (selectedStatus, entry) =>
          entry.activityStatus === selectedStatus,
      },
    );
  }

  columns = [
    ...columns,
    {
      title: 'State',
      dataIndex: 'state',
      filters: stateFilterOptions,
      onFilter: (selectedState, entry) => entry.state === selectedState,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      filters: typeFilterOptions,
      onFilter: (selectedType, entry) => entry.type === selectedType,
    },
    {
      title: 'Detail',
      render: (transaction) => {
        if (transaction.sourceActivityType === TRANSACTION_TYPE.PAYMENT) {
          return null;
        }

        let additionalActions = [];

        if (showAdditionalActions) {
          if (transaction.isPendingAuthorization) {
            let label = 'Release OTB';
            let disabled = false;

            if (!enableOTBRelease) {
              label = 'OTB Release Ineligible';
              disabled = true;
            }

            additionalActions.push({
              label,
              key: ACTION_KEY.REVERSE_AUTHORIZATION,
              disabled,
            });
          }

          if (FEE_DESCRIPTIONS.includes(transaction.merchantChannel)) {
            let disabled = false;
            let label = 'Waive this fee';

            if (accountInDebtSale) {
              disabled = true;
              label = 'Fee Waiver Ineligible - Debt Sale';
            }

            additionalActions.push({
              label,
              key: ACTION_KEY.WAIVE_FEE,
              disabled,
            });
          }

          if (!additionalActions.length) {
            additionalActions.push({
              label: 'No Additional Actions',
              key: '0',
              disabled: true,
            });
          }
        }

        return (
          <Space.Compact
            data-testid={`detail-button-${transaction.transactionId}`}
            size="small"
          >
            <Button onClick={() => defaultAction(transaction)}>Detail</Button>
            {showAdditionalActions ? (
              <Dropdown
                menu={{
                  items: additionalActions,
                  onClick: (e) => menuItemAction(e, transaction),
                }}
                trigger={['click']}
              >
                <Button icon={<EllipsisOutlined />} />
              </Dropdown>
            ) : null}
          </Space.Compact>
        );
      },
    },
  ];

  return columns;
};
